import React, { useEffect, useMemo, useState } from "react";
import config from "./config";
import "./ServiceStatusPage.css";

const SERVICE_STATUS_PAGES = {
  zoom: {
    serviceId: 1,
    displayName: "Zoom",
    logoUrl: "https://www.signaltuner.com/images/is-service-down/zoom.png",
    description: "Live Zoom status and recent incident history, with optional email alerts when things change.",
  },
  teams: {
    serviceId: 3,
    displayName: "Microsoft Teams",
    logoUrl: "https://www.signaltuner.com/images/is-service-down/msteams.png",
    description: "Live Microsoft Teams status and recent incident history, with optional email alerts when things change.",
  },
  microsoft365: {
    serviceId: 41,
    displayName: "Microsoft 365",
    logoUrl: "https://www.signaltuner.com/images/is-service-down/microsoft365.png",
    description: "Live Microsoft 365 status and recent incident history, with optional email alerts when things change.",
  },
};

const API_URL = `${config.API_URL}api/User`;

const getStatusStyle = (status) => {
  switch (status) {
    case "Operational":
      return { borderColor: "#BDE9CB", backgroundColor: "#E4F7EA" };
    case "Active incident":
      return { borderColor: "#FFD9A3", backgroundColor: "#FFF3E2" };
    case "Outage":
      return { borderColor: "#FAC6C6", backgroundColor: "#FFF1F1" };
    default:
      return {};
  }
};

function ServiceStatusPage({ serviceKey }) {
  const service = SERVICE_STATUS_PAGES[serviceKey];
  const [serviceStatus, setServiceStatus] = useState(null);
  const [serviceIncidents, setServiceIncidents] = useState([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signupMessage, setSignupMessage] = useState(null);

  useEffect(() => {
    const description = document.querySelector('meta[name="description"]');
    if (description) {
      description.setAttribute("content", service.description);
    }
  }, [service.description]);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError(null);

    Promise.all([
      fetch(`${API_URL}/GetServiceStatus/${service.serviceId}`).then((response) => {
        if (!response.ok) {
          throw new Error(`Service status request failed: ${response.statusText}`);
        }
        return response.json();
      }),
      fetch(`${API_URL}/GetServiceIncidents/${service.serviceId}`).then((response) => {
        if (!response.ok) {
          throw new Error(`Service incidents request failed: ${response.statusText}`);
        }
        return response.json();
      }),
    ])
      .then(([status, incidents]) => {
        if (cancelled) {
          return;
        }

        setServiceStatus(status);
        setServiceIncidents(Array.isArray(incidents) ? incidents : []);
      })
      .catch((fetchError) => {
        if (!cancelled) {
          setError(fetchError.message);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [service.serviceId]);

  const sortedServiceIncidents = useMemo(
    () =>
      [...serviceIncidents].sort((a, b) => {
        if (a.Status === "Active" && b.Status !== "Active") return -1;
        if (a.Status !== "Active" && b.Status === "Active") return 1;
        return new Date(b.PublishDate) - new Date(a.PublishDate);
      }),
    [serviceIncidents]
  );

  const displayName = serviceStatus?.DisplayName || service.displayName;
  const currentStatus = serviceStatus?.CurrentStatus || "Unknown";

  const handleSignup = () => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailPattern.test(email)) {
      alert("Please enter a valid email address.");
      return;
    }

    fetch(`${API_URL}/AlertEmailSignup`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, serviceId: service.serviceId }),
    })
      .then((response) => {
        if (response.status === 409) {
          alert("This email is already subscribed.");
          throw new Error("Email already subscribed");
        }

        if (!response.ok) {
          throw new Error(`Email signup failed: ${response.statusText}`);
        }

        return response.json();
      })
      .then((data) => {
        setSignupMessage(data.message);
        setEmail("");
      })
      .catch((signupError) => {
        if (signupError.message !== "Email already subscribed") {
          setSignupMessage("An error occurred while signing up. Please try again.");
        }
      });
  };

  return (
    <main className="service-status-page">
      <div className="service-status-shell">
        <section className="service-status-hero-card">
          <img className="service-status-service-logo" src={service.logoUrl} alt={`${displayName} logo`} />
          <div className="service-status-meta">
            <span>Current status</span>
            <div className="service-status-row">
              <h1>{displayName}</h1>
              <span className="service-status-pill" style={getStatusStyle(currentStatus)}>
                {currentStatus}
              </span>
            </div>
            <p>Live status and recent incident history for {displayName}, with optional email alerts when things change.</p>
          </div>

          {loading && <p className="service-status-feedback">Loading...</p>}
          {error && <p className="service-status-feedback service-status-error">Error: {error}</p>}
        </section>

        <section className="service-status-signup-card">
          <div className="service-status-signup-copy">
            <h2>Get alerts for incidents and outages</h2>
            <p>Subscribe with your email to hear about new issues affecting {displayName}.</p>
            <div className="service-status-powered-by">
              <span>Powered by</span>
              <img src="https://www.signaltuner.com/images/SignalTUNER%20Logo%20Horizontal-01.png" alt="SignalTuner logo" />
            </div>
          </div>

          <div className="service-status-email-row">
            <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="Enter your email" />
            <button type="button" onClick={handleSignup}>
              Submit
            </button>
          </div>

          {signupMessage && <p className="service-status-feedback">{signupMessage}</p>}
        </section>

        <section className="service-status-incidents-card">
          <div className="service-status-section-heading">
            <h2>Recent incidents</h2>
            <p>The latest published incidents, with active items pinned to the top.</p>
          </div>

          {sortedServiceIncidents.length > 0 && (
            <div className="service-status-incidents-list">
              {sortedServiceIncidents.slice(0, 50).map((incident) => (
                <ExpandableIncident key={incident.IncidentId} incident={incident} serviceName={displayName} />
              ))}
            </div>
          )}

          {sortedServiceIncidents.length === 0 && !loading && <p className="service-status-empty-state">No recent incidents reported.</p>}
        </section>
      </div>
    </main>
  );
}

function ExpandableIncident({ incident, serviceName }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = () => {
    setIsExpanded((current) => !current);
  };

  return (
    <article className="service-status-incident">
      <button type="button" className="service-status-incident-toggle" onClick={toggleExpanded} aria-expanded={isExpanded}>
        <div>
          <h3>{incident.Title}</h3>
          <p>
            Status
            <span className="service-status-incident-pill" data-active={incident.Status === "Active"}>
              {incident.Status}
            </span>
          </p>
        </div>
        <span className="service-status-expand-icon" data-expanded={isExpanded} aria-hidden="true">
          >
        </span>
      </button>

      {isExpanded && (
        <div className="service-status-incident-content">
          <div dangerouslySetInnerHTML={{ __html: incident.Description }} />
          <a href={incident.Link} target="_blank" rel="noopener noreferrer">
            {serviceName} incident report
          </a>
          <p>Published on: {new Date(incident.PublishDate).toLocaleDateString()}</p>
        </div>
      )}
    </article>
  );
}

export const ZoomStatus = () => <ServiceStatusPage serviceKey="zoom" />;
export const TeamsStatus = () => <ServiceStatusPage serviceKey="teams" />;
export const Microsoft365Status = () => <ServiceStatusPage serviceKey="microsoft365" />;

export default ServiceStatusPage;
